import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Search, Filter, Plus, DollarSign, Eye } from 'lucide-react';
import { fetchPlacements } from '@/lib/api';
import type { Placement } from '@/types';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PlacementForm } from '@/components/placements/PlacementForm';
import { formatCurrency, formatDate, capitalizeFirst } from '@/lib/utils';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function getStatusBadgeVariant(status: string): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'active':
      return 'success';
    case 'completed':
      return 'default';
    case 'cancelled':
      return 'destructive';
    default:
      return 'secondary';
  }
}

export function PlacementsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const pageSize = 10;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  const params = useMemo(() => ({
    page,
    limit: pageSize,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  }), [page, statusFilter]);

  const { data, isLoading } = useQuery({
    queryKey: ['placements', params, debouncedSearch],
    queryFn: () => fetchPlacements(params),
  });

  // Client-side company name filtering (search by candidate, job, or company)
  const filteredData = useMemo(() => {
    if (!data?.data || !debouncedSearch) return data?.data ?? [];
    const q = debouncedSearch.toLowerCase();
    return data.data.filter((p) =>
      `${p.candidate.firstName} ${p.candidate.lastName}`.toLowerCase().includes(q) ||
      p.job.title.toLowerCase().includes(q) ||
      p.company.name.toLowerCase().includes(q)
    );
  }, [data?.data, debouncedSearch]);

  const columns: ColumnDef<Placement>[] = useMemo(
    () => [
      {
        accessorKey: 'candidate',
        header: 'Candidate',
        cell: ({ row }) => {
          const placement = row.original;
          return (
            <Link
              to={`/candidates/${placement.candidateId}`}
              className="font-medium hover:text-emerald-500"
            >
              {placement.candidate.firstName} {placement.candidate.lastName}
            </Link>
          );
        },
      },
      {
        accessorKey: 'job',
        header: 'Job Title',
        cell: ({ row }) => {
          const placement = row.original;
          return (
            <Link
              to={`/jobs/${placement.jobId}`}
              className="text-sm hover:text-emerald-500"
            >
              {placement.job.title}
            </Link>
          );
        },
      },
      {
        accessorKey: 'company',
        header: 'Company',
        cell: ({ row }) => {
          const placement = row.original;
          return (
            <Link
              to={`/companies/${placement.companyId}`}
              className="text-sm hover:text-emerald-500"
            >
              {placement.company.name}
            </Link>
          );
        },
      },
      {
        accessorKey: 'startDate',
        header: 'Start Date',
        cell: ({ row }) => {
          const date = row.original.startDate;
          if (!date) return <span className="text-text-tertiary">-</span>;
          return <span className="text-sm">{formatDate(date)}</span>;
        },
      },
      {
        accessorKey: 'feeAmount',
        header: 'Fee',
        cell: ({ row }) => {
          const placement = row.original;
          if (placement.feeAmount == null) return <span className="text-text-tertiary">-</span>;
          return (
            <div className="flex items-center gap-1 text-sm">
              <DollarSign className="h-3.5 w-3.5 text-text-tertiary" />
              {formatCurrency(placement.feeAmount, placement.salaryCurrency || 'ZAR')}
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          return (
            <Badge variant={getStatusBadgeVariant(status)}>
              {capitalizeFirst(status)}
            </Badge>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const placement = row.original;
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/placements/${placement.id}`)}
            >
              <Eye className="h-4 w-4" />
            </Button>
          );
        },
      },
    ],
    [navigate],
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Placements</h1>
          <p className="text-text-secondary mt-1">
            Track and manage candidate placements
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Placement
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
            <Input
              placeholder="Search by candidate, job, or company..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Status Filter */}
          <div className="flex gap-2">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-text-tertiary" />
            </div>

            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen">
        <DataTable
          columns={columns}
          data={debouncedSearch ? filteredData : (data?.data ?? [])}
          page={page}
          pageSize={pageSize}
          total={debouncedSearch ? filteredData.length : (data?.pagination.total ?? 0)}
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="No placements found. Try adjusting your filters."
          getRowId={(row) => row.id}
        />
      </div>

      {/* Create Placement Dialog */}
      <PlacementForm
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={() => {
          setShowCreateDialog(false);
        }}
      />
    </div>
  );
}
